<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onScopeDispose,
    ref,
    watch,
  } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import PosUiSwitch from '@/components/pos-v2/ui/PosUiSwitch.vue';
  import V2ReservationPartyQuickRow from '@/components/v2/reservations/V2ReservationPartyQuickRow.vue';
  import V2ReservationDayStrip from '@/components/v2/reservations/V2ReservationDayStrip.vue';
  import V2ReservationSlotPicker from '@/components/v2/reservations/V2ReservationSlotPicker.vue';
  import V2ReservationGuestLookup from '@/components/v2/reservations/V2ReservationGuestLookup.vue';
  import V2ReservationDepositBadge from '@/components/v2/reservations/V2ReservationDepositBadge.vue';
  import type { IReservationFormGuest } from '@/components/v2/reservations/reservation-form.interfaces';
  import type { IReservation } from '@/interfaces/IReservation';
  import { useReservationForm } from '@/composition/reservations/use-reservation-form';
  import type { IReservationFormDraft } from '@/composition/reservations/use-reservation-form';
  import V2ReservationFormExtraDetails from '@/components/v2/reservations/V2ReservationFormExtraDetails.vue';
  import { useAuthStore } from '@/composition/use-auth';
  import { useModal } from '@/composition/use-modal';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { useUi } from '@/composition/use-ui';
  import { buildPosTableViews } from '@/domain/pos/table-views';
  import { getReservationTimeZone } from '@/domain/reservations/reservation-time';
  import { OrderType } from '@/enums/OrderEnums';
  import { PermissionType } from '@/enums/PermissionType';
  import { AlertType } from '@/interfaces/IAlert';
  import { useOrdersStore } from '@/stores/orders.store';
  import { useReservationsStore } from '@/stores/reservations.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  const props = defineProps<{
    date?: string;
    startsAt?: string;
    tableIds?: number[];
    partySize?: number;
    guest?: IReservationFormGuest;
    reservationToEdit?: IReservation;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const modals = useModal();
  const ui = useUi();
  const money = usePosMoney();
  const auth = useAuthStore();
  const restaurant = useRestaurantStore();
  const reservations = useReservationsStore();
  const orders = useOrdersStore();
  const modalName = 'v2-reservation-form';
  const formElement = ref<HTMLFormElement | null>(null);
  const more = ref(!!props.reservationToEdit || !!props.tableIds?.length);
  let alive = true;
  onScopeDispose(() => {
    alive = false;
  });
  const canManage = computed(() =>
    auth.permissionSet.has(PermissionType.ReservationsManage)
  );
  const zone = computed(() =>
    getReservationTimeZone(
      restaurant.selectedBranch?.location,
      restaurant.restaurant?.country
    )
  );
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const initialBranch = restaurant.selectedBranch?.id;
  const initialRestaurant = restaurant.restaurant?.id;
  watch(
    () => [restaurant.selectedBranch?.id, restaurant.restaurant?.id],
    () => {
      if (
        restaurant.selectedBranch?.id !== initialBranch ||
        restaurant.restaurant?.id !== initialRestaurant
      )
        modals.hide(modalName);
    },
    { flush: 'sync' }
  );
  const form = useReservationForm(props, {
    context: () => ({
      restaurantId: restaurant.restaurant?.id ?? 0,
      branchId: restaurant.selectedBranch?.id ?? 0,
      country: restaurant.restaurant?.country ?? 'AR',
      timeZone: zone.value,
      businessDayStartHour: restaurant.selectedBranch?.businessDayStartHour,
      tables:
        buildPosTableViews(
          restaurant.tables,
          orders.pendingOrdersByType?.[OrderType.Seat] ?? [],
          ''
        ) ?? [],
    }),
    saved: (row, deposit) => {
      reservations.patchReservation(row);
      ui.showToast(
        AlertType.Success,
        tr(
          props.reservationToEdit
            ? 'reservations.form.updated'
            : deposit
            ? 'reservations.form.createdDeposit'
            : 'reservations.form.created'
        )
      );
      proxy.$parent?.$emit('saved', row);
      modals.hide(modalName);
    },
    error: (cause) => ui.serverError(cause, tr('reservations.form.error')),
  });
  const {
    draft,
    slots,
    inventory,
    loading,
    failed,
    saving,
    errors,
    conflict,
    alternatives,
    durationMin,
    slotStates,
    forcePrompt,
    depositRequired,
    depositAmount,
    minimumDate,
  } = form;
  const disabled = computed(() => saving.value || !canManage.value);
  const depositItems = computed(() => [
    {
      id: 'collect',
      label: tr('reservations.form.depositCollect', {
        amount: money.formatCurrency(depositAmount.value ?? 0),
      }),
    },
    { id: 'skip', label: tr('reservations.form.depositSkip') },
  ]);
  const extraDraft = computed({
    get: () => draft,
    set: (value: IReservationFormDraft) => {
      if (disabled.value) return;
      if (value.source !== draft.source)
        form.sourceChanged.value =
          value.source !== props.reservationToEdit?.source;
      Object.assign(draft, value);
    },
  });
  function error(key: string) {
    return errors.value[key] ? tr(errors.value[key]) : undefined;
  }
  function setDeposit(value: string) {
    if (!disabled.value) draft.collectDeposit = value === 'collect';
  }
  async function save() {
    if (disabled.value) return;
    const result = await form.submit();
    if (!alive || result) return;
    if (errors.value.email || errors.value.tables) more.value = true;
    await nextTick();
    if (!alive) return;
    const key = Object.keys(errors.value)[0];
    const section =
      key === 'name' || key === 'phone'
        ? 'guest'
        : key === 'email' || key === 'tables'
        ? 'details'
        : key;
    formElement.value
      ?.querySelector(`[data-section="${section}"]`)
      ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
</script>
<template>
  <form
    ref="formElement"
    class="reservation-form-modal"
    novalidate
    @submit.prevent="save"
  >
    <header class="modal-header">
      <h2>
        {{
          $t(
            reservationToEdit
              ? 'reservations.form.editTitle'
              : 'reservations.form.newTitle'
          )
        }}
      </h2>
    </header>
    <div class="modal-content">
      <section data-section="party" class="form-section">
        <PosUiField
          :label="$t('reservations.form.party')"
          :error="error('party')"
          required
          ><V2ReservationPartyQuickRow
            v-model="draft.partySize"
            :disabled="disabled"
        /></PosUiField>
      </section>
      <section data-section="date" class="form-section">
        <PosUiField :label="$t('reservations.form.date')" required
          ><V2ReservationDayStrip
            v-model="draft.date"
            :time-zone="zone"
            :minimum-date="minimumDate"
            :disabled="disabled"
            :error="error('date')"
        /></PosUiField>
      </section>
      <section data-section="time" class="form-section">
        <PosUiField :label="$t('reservations.form.time')" required
          ><V2ReservationSlotPicker
            :value="draft.startsAt"
            :payload="slots"
            :states="slotStates"
            :loading="loading"
            :failed="failed"
            :disabled="disabled"
            :error="error('time')"
            :conflict="conflict"
            :alternatives="alternatives"
            @input="form.chooseSlot"
            @force-request="form.requestForce"
            @retry="form.load()"
        /></PosUiField>
        <div v-if="forcePrompt" class="force-control">
          <label
            ><span>{{ $t('reservations.form.force') }}</span
            ><PosUiSwitch
              v-model="draft.force"
              :disabled="disabled"
              :aria-label="$t('reservations.form.force')"
          /></label>
          <p>{{ $t('reservations.form.forceHint') }}</p>
        </div>
      </section>
      <section data-section="guest" class="form-section">
        <h3>{{ $t('reservations.form.guest') }}</h3>
        <V2ReservationGuestLookup
          v-model="draft.guest"
          :restaurant-id="restaurant.restaurant ? restaurant.restaurant.id : 0"
          :branch-id="
            restaurant.selectedBranch ? restaurant.selectedBranch.id : 0
          "
          :country="
            restaurant.restaurant ? restaurant.restaurant.country : 'AR'
          "
          :disabled="disabled"
          :name-error="error('name')"
          :phone-error="error('phone')"
        />
      </section>
      <section
        v-if="reservationToEdit"
        data-section="deposit"
        class="form-section"
      >
        <PosUiField :label="$t('reservations.form.deposit')">
          <div class="deposit-summary">
            <span v-if="reservationToEdit.depositStatus === 'none'">
              {{ $t('reservations.form.depositSkip') }}
            </span>
            <template v-else>
              <span v-if="reservationToEdit.depositStatus === 'paid'">
                {{
                  $t('reservations.pos.depositPaid', {
                    amount: money.formatComputedMoney(
                      reservationToEdit.depositAmount ?? 0
                    ),
                  })
                }}
              </span>
              <span v-else class="deposit-amount">
                {{
                  money.formatComputedMoney(
                    reservationToEdit.depositAmount ?? 0
                  )
                }}
              </span>
              <V2ReservationDepositBadge
                v-if="reservationToEdit.depositStatus !== 'paid'"
                :status="reservationToEdit.depositStatus"
                :amount="reservationToEdit.depositAmount"
              />
            </template>
          </div>
        </PosUiField>
      </section>
      <section
        v-else-if="depositRequired"
        data-section="deposit"
        class="form-section"
      >
        <PosUiField
          :label="$t('reservations.form.deposit')"
          :hint="$t('reservations.form.depositHint')"
          ><PosUiSegmented
            :value="draft.collectDeposit ? 'collect' : 'skip'"
            :items="depositItems"
            :disabled="disabled"
            @input="setDeposit"
        /></PosUiField>
      </section>
      <PosUiButton
        class="more-toggle"
        color="neutral"
        variant="ghost"
        :icon="more ? 'chevron-up' : 'chevron-down'"
        :aria-expanded="more"
        @click="more = !more"
        >{{
          $t(
            more
              ? 'reservations.form.lessDetails'
              : 'reservations.form.moreDetails'
          )
        }}</PosUiButton
      >
      <V2ReservationFormExtraDetails
        v-show="more"
        v-model="extraDraft"
        :errors="errors"
        :inventory="inventory"
        :slots="slots"
        :duration-min="durationMin"
        :sectors="restaurant.sectors || []"
        :disabled="disabled"
        :reservation-to-edit="reservationToEdit"
      />
    </div>
    <footer class="modal-actions">
      <PosUiButton
        color="neutral"
        variant="outline"
        :disabled="saving"
        @click="modals.hide(modalName)"
        >{{ $t('cancel') }}</PosUiButton
      ><PosUiButton type="submit" :disabled="disabled || loading">{{
        $t(
          reservationToEdit
            ? 'reservations.form.submitEdit'
            : 'reservations.form.submit'
        )
      }}</PosUiButton>
    </footer>
  </form>
</template>
<style scoped lang="scss">
  .reservation-form-modal {
    @apply tw-flex tw-min-h-0 tw-w-full tw-flex-col tw-bg-white;
    max-height: 90dvh;
    .modal-header {
      @apply tw-flex-none tw-border-b tw-border-neutral-200 tw-px-5 tw-py-4;
      h2 {
        @apply tw-m-0 tw-text-lg tw-font-semibold tw-text-neutral-900;
      }
    }
    .modal-content {
      @apply tw-flex tw-min-h-0 tw-flex-col tw-gap-6 tw-overflow-y-auto tw-p-5;
    }
    .form-section {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-3;
      h3 {
        @apply tw-m-0 tw-text-sm tw-font-medium tw-text-neutral-900;
      }
    }
    .deposit-summary {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm tw-text-neutral-700;
      .deposit-amount {
        @apply tw-font-semibold tw-tabular-nums tw-text-neutral-900;
      }
    }
    .modal-actions {
      @apply tw-flex tw-flex-none tw-justify-end tw-gap-2 tw-border-t tw-border-neutral-200 tw-bg-white tw-p-4;
    }
    .more-toggle {
      @apply tw-self-start;
    }
    .force-control label {
      @apply tw-flex tw-items-center tw-justify-between tw-gap-3 tw-text-sm tw-text-neutral-800;
    }
    .force-control {
      @apply tw-rounded-lg tw-border tw-border-solid tw-border-amber-200 tw-bg-amber-50 tw-p-3;
      p {
        @apply tw-m-0 tw-mt-2 tw-text-xs tw-text-amber-800;
      }
    }
    :deep(.pos-ui-segmented-option) {
      @apply tw-min-h-11 tw-h-auto tw-whitespace-normal tw-py-2;
    }
  }
</style>
